import Spinner from '../../../../components/spinner';
import Select from '../../../../components/select';
import {useMemo, useCallback} from 'react';
import MultiSelectAutocomplete from '../../../../components/multi-select-autocomplete';
import FilterTags from '../filter-tags';
import {FilterProps} from './filter-types';
import {parseFilterValues, joinFilterValues} from './utils/parse-filter-values';

export default function NOFilter(props: FilterProps) {
    const {label, data, value, onChange, children, spinner, disabled, searchable, required, itemToString, placeholder, enableMultiSelect, maxSelections, sortSelectedFirst, enableFuseSearch = true, fuseThreshold = 0.3, fuseKeys, showPrimaryIndicator = false} = props;
    const hideTags = enableMultiSelect ? ((props as any).hideTags ?? true) : true;

    const parsedValues = useMemo(() => parseFilterValues(value), [value]);

    const titleText = (
        <div className='flex flex-row items-center'>
            {label && <label className='block text-gray-500 text-sm mb-1'>{label}</label>}
            {!disabled && spinner && (
                <div className='ml-2' data-testid={props['data-testid'] ? `${props['data-testid']}-spinner` : undefined}>
                    <Spinner />
                </div>
            )}
        </div>
    );

    const handleRemoveTag = useCallback((removedValue: string) => {
        const newValues = parsedValues.filter(v => v !== removedValue);
        onChange(joinFilterValues(newValues));
    }, [parsedValues, onChange]);

    const itemToStringForTags = useCallback(
        (item: string) => (itemToString ? itemToString(item, 0) : item),
        [itemToString]
    );

    const getOptions = useCallback(() => Promise.resolve(data), [data]);

    return (
        <div className='filter-group mb-4'>
            {enableMultiSelect ? (
                <>
                    {/*Putting titleText inside label with class cds--label, fixes its css to align with other filters */}
                    <label className='cds--label'>{titleText}</label>
                    {!hideTags && parsedValues.length > 0 && (
                        <FilterTags
                            values={parsedValues}
                            onRemove={handleRemoveTag}
                            itemToString={itemToStringForTags}
                            showPrimaryIndicator={showPrimaryIndicator}
                        />
                    )}
                    <MultiSelectAutocomplete
                        onChange={(values: string[]) => onChange(joinFilterValues(values))}
                        values={parsedValues}
                        getOptions={getOptions}
                        useNewDesign={true}
                        disabled={disabled || spinner}
                        label={label}
                        maximumSelectionLength={maxSelections}
                        sortSelectedFirst={sortSelectedFirst}
                        enableFuseSearch={enableFuseSearch}
                        fuseThreshold={fuseThreshold}
                        itemToString={itemToStringForTags}
                        {...(props['data-testid'] ? {'data-testid': props['data-testid']} : {})}
                    />
                </>
            ) : (
                <Select
                    key={!value ? 'remount-' + Date.now() : 'normal'}
                    data={data}
                    value={value}
                    onChange={onChange}
                    disabled={disabled || spinner}
                    titleText={titleText}
                    searchable={searchable}
                    required={required}
                    itemToString={itemToString}
                    placeholder={placeholder}
                    autoAlign
                    rightAlign
                    enableFuseSearch={enableFuseSearch}
                    fuseThreshold={fuseThreshold}
                    fuseKeys={fuseKeys}
                    {...(props['data-testid'] ? {'data-testid': props['data-testid']} : {})}
                >
                    {children}
                </Select>
            )}
        </div>
    );
}
